import { FC, useEffect, useState } from "react";
import "./ModalSortSubsectionsInsert.scss";
import ModalDefault from "../../components/ModalDefault/ModalDefault";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuth } from "../../providers/AuthProvider";
import { useForm } from "react-hook-form";
import { Card, Col, FormGroup, ListGroup, Row } from "react-bootstrap";
import ErrorMessage from "../../components/ErrorMessage/ErrorMessage";
import { schema } from "./Validate";
import ReactSelect from "react-select";
import { useAgentsCategories } from "../../providers/Agents/AgentsCategories/AgentsCategoriesProvider";
import { customStyles } from "../../models/SelectCustomStyles";
import { useSections } from "../../providers/Articles/Sections/SectionsProvider";
import { useSubsections } from "../../providers/Articles/Subsections/SubsectionsProvider";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";

interface ModalSortSubsectionsInsertProps {
  show: boolean;
  onClose: () => void;
}

const ModalSortSubsectionsInsert: FC<ModalSortSubsectionsInsertProps> = ({
  show,
  onClose,
}) => {
  const [selectedCategories, setSelectedCategories] = useState<any | null>(
    null,
  );
  const [selectedSection, setSelectedSection] = useState<any | null>(null);
  const [sortSubsectionsInfo, setSortSubsectionsInfo] = useState<any | null>(
    null,
  );
  const { onInvalid } = useAuth();

  const {
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const { categoriesOptions, setParams: setParamsAgentsCategories } =
    useAgentsCategories();
  const { sectionsOptions, handleListSectionsOption } = useSections();
  const {
    handleListSubsectionsOption,
    subsectionsOptions,
    handleSortSubsections,
  } = useSubsections();

  const handleClose = () => {
    setValue("category_id", null);
    setValue("section_id", null);
    setSelectedCategories(null);
    setSelectedSection(null);
    setSortSubsectionsInfo(null);
    onClose();
  };

  useEffect(() => {
    if (show) {
      setParamsAgentsCategories({
        withPaginate: false,
        filter: { only_active: true },
      });
    }
  }, [show]);

  useEffect(() => {
    setValue("category_id", selectedCategories?.value || null);
    if (selectedCategories) {
      handleListSectionsOption({
        withPaginate: false,
        filter: { only_active: true, category_id: selectedCategories?.value },
      });
    }
  }, [selectedCategories]);

  useEffect(() => {
    setValue("section_id", selectedSection?.value || null);
    if (selectedSection) {
      handleListSubsectionsOption({
        withPaginate: false,
        filter: { only_active: true, section_id: selectedSection?.value },
      });
    }
  }, [selectedSection]);

  useEffect(() => {
    if (selectedCategories && selectedSection && subsectionsOptions) {
      setSortSubsectionsInfo(subsectionsOptions);
    }
  }, [subsectionsOptions]);

  const handleOnDragEnd = async (result: any) => {
    if (!result.destination) return;

    const items = Array.from(sortSubsectionsInfo);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSortSubsectionsInfo(items);
    await handleSortSubsections({
      subsections: items.map((item: any, index: number) => ({
        id: item.value,
        order: index,
      })),
    });
  };

  return (
    <ModalDefault
      title="Ordenar Subseções"
      show={show}
      onClose={handleClose}
      sizeModal={"xl"}
      showFooter={true}
      backdrop="static"
      backgroundColor="#f8f9fa"
    >
      <div
        className="ModalSortSubsectionsInsert"
        data-testid="ModalSortSubsectionsInsert"
      >
        <Row>
          <Col md={5}>
            <Row>
              <Col md={12}>
                <FormGroup className="form-group">
                  <label htmlFor="category_id">CATEGORIA</label>
                  <ReactSelect
                    isSearchable
                    isClearable
                    options={categoriesOptions ?? []}
                    value={selectedCategories}
                    placeholder="Selecione"
                    className={`form-control p-0 ${onInvalid(
                      errors?.category_id,
                    )}`}
                    noOptionsMessage={() => "Não há registros"}
                    styles={customStyles}
                    onChange={(e) => {
                      setSelectedCategories(e ?? null);
                    }}
                  />
                  <ErrorMessage
                    name={"Categoria"}
                    type={errors?.category_id?.type?.toString()}
                  />
                </FormGroup>
              </Col>
              <Col md={12}>
                <FormGroup className="form-group">
                  <label htmlFor="section_id">SEÇÃO</label>
                  <ReactSelect
                    isSearchable
                    isClearable
                    options={sectionsOptions ?? []}
                    value={selectedSection}
                    placeholder="Selecione"
                    className={`form-control p-0 ${onInvalid(
                      errors?.section_id,
                    )}`}
                    noOptionsMessage={() => "Não há registros"}
                    styles={customStyles}
                    onChange={(e) => {
                      setSelectedSection(e ?? null);
                    }}
                  />
                  <ErrorMessage
                    name={"Seção"}
                    type={errors?.section_id?.type?.toString()}
                  />
                </FormGroup>
              </Col>
            </Row>
          </Col>
          <Col md={7}>
            <Card>
              <div className="p-4">
                <h5 className="mb-4">Subseções</h5>

                <ListGroup className="navbar-nav subsections">
                  <DragDropContext onDragEnd={handleOnDragEnd}>
                    <Droppable droppableId="pages">
                      {(provided: any) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                        >
                          {sortSubsectionsInfo &&
                          sortSubsectionsInfo?.length > 0 ? (
                            <>
                              {sortSubsectionsInfo?.map(
                                (item: any, key: number) => (
                                  <Draggable
                                    key={item?.value || key}
                                    draggableId={
                                      item?.value?.toString() ||
                                      key?.toString() ||
                                      ""
                                    }
                                    index={key}
                                  >
                                    {(provided: any, snapshot: any) => (
                                      <div className="ps-3 pe-1" key={key}>
                                        <div
                                          className={`nav-link text-wrap d-flex align-items-center py-2 ${
                                            snapshot.isDragging
                                              ? "background"
                                              : ""
                                          }`}
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                        >
                                          <div
                                            className="text-center"
                                            {...provided.dragHandleProps}
                                          >
                                            <i className="fas fa-grip-vertical"></i>
                                          </div>
                                          <span className="nav-link-text ms-2">
                                            {item?.label || ""}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                ),
                              )}
                            </>
                          ) : (
                            <div className="d-flex justify-content-center">
                              Não há registro
                            </div>
                          )}

                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </ListGroup>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </ModalDefault>
  );
};

export default ModalSortSubsectionsInsert;
